import {
  Box,
  List,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  ListItemButton,
  useTheme,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import FactoryIcon from '@mui/icons-material/Factory';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../auth';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { TenantSelectIfAdmin } from '../../../shared/components/SelectsDinamycs/TenantSelectIfAdmin';
import { Book as BookIcon, BookOnlineOutlined } from '@mui/icons-material';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import { ROLES } from '../../../shared/constants/roles';
import type { Role } from '../../../shared/constants/roles';
import { useSettings } from '../../settings/context/SettingsContext';

interface UserRole {
  id: string;
  name: Role;
  description: string;
}

const navItems = [
  {
    label: 'Dashboard',
    icon: <DashboardRoundedIcon fontSize="medium" />,
    to: '/dashboard',
    roles: [ROLES.ADMIN, ROLES.GESTOR, ROLES.USER],
  },
  {
    label: 'Empresas',
    icon: <ApartmentRoundedIcon fontSize="medium" />,
    to: '/tenants',
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Usuários',
    icon: <Groups2RoundedIcon fontSize="medium" />,
    to: '/users',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Moradores',
    icon: <ContactEmergencyIcon fontSize="medium" />,
    to: '/residents',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Áreas',
    icon: <MeetingRoomIcon fontSize="medium" />,
    to: '/rooms',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Equipamentos',
    icon: <SettingsRoundedIcon fontSize="medium" />,
    to: '/equipments',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Armários',
    icon: <LockPersonIcon fontSize="medium" />,
    to: '/lockers',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Fabricantes',
    icon: <FactoryIcon fontSize="medium" />,
    to: '/manufacturers',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Visitantes',
    icon: <ContactPhoneIcon fontSize="medium" />,
    to: '/visitors',
    roles: [ROLES.GESTOR, ROLES.USER, ROLES.ADMIN],
  },
  {
    label: 'Reservas',
    icon: <CalendarMonthIcon fontSize="medium" />,
    to: '/reservations',
    roles: [ROLES.GESTOR, ROLES.USER, ROLES.ADMIN],
  },
  {
    label: 'Anúncios',
    icon: <AnnouncementIcon fontSize="medium" />,
    to: '/announcements',
    roles: [ROLES.GESTOR, ROLES.USER, ROLES.ADMIN],
  },
  {
    label: 'Relatórios',
    icon: <BookIcon fontSize="medium" />,
    to: '/reports',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Setores',
    icon: <MapsHomeWorkIcon fontSize="medium" />,
    to: '/sectors',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Unidades',
    icon: <AutoAwesomeMosaicIcon fontSize="medium" />,
    to: '/units',
    roles: [ROLES.GESTOR, ROLES.ADMIN],
  },
  {
    label: 'Encomendas',
    icon: <BookOnlineOutlined fontSize="medium" />,
    to: '/orders',
    roles: [ROLES.GESTOR, ROLES.USER, ROLES.ADMIN],
  },
];

// Função utilitária para mapear o nome do ícone (string) para o componente React
const iconMap: Record<string, JSX.Element> = {
  DashboardRoundedIcon: <DashboardRoundedIcon fontSize="medium" />,
  ApartmentRoundedIcon: <ApartmentRoundedIcon fontSize="medium" />,
  Groups2RoundedIcon: <Groups2RoundedIcon fontSize="medium" />,
  ContactEmergencyIcon: <ContactEmergencyIcon fontSize="medium" />,
  MeetingRoomIcon: <MeetingRoomIcon fontSize="medium" />,
  SettingsRoundedIcon: <SettingsRoundedIcon fontSize="medium" />,
  LockPersonIcon: <LockPersonIcon fontSize="medium" />,
  FactoryIcon: <FactoryIcon fontSize="medium" />,
  ContactPhoneIcon: <ContactPhoneIcon fontSize="medium" />,
  CalendarMonthIcon: <CalendarMonthIcon fontSize="medium" />,
  AnnouncementIcon: <AnnouncementIcon fontSize="medium" />,
  BookIcon: <BookIcon fontSize="medium" />,
  MapsHomeWorkIcon: <MapsHomeWorkIcon fontSize="medium" />,
  AutoAwesomeMosaicIcon: <AutoAwesomeMosaicIcon fontSize="medium" />,
  BookOnlineOutlined: <BookOnlineOutlined fontSize="medium" />,
};

// Função para obter os itens do menu (customizado ou default)
function useSidebarMenuItems() {
  const { settings } = useSettings();
  const menuItemsConfig = settings?.config?.menuItems;
  const hasConfig = Array.isArray(menuItemsConfig) && menuItemsConfig.length > 0;

  if (hasConfig) {
    // Mapear do formato settings para o formato do sidebar
    return menuItemsConfig.map((item: any) => ({
      label: item.label,
      icon: iconMap[item.icon] || <DashboardRoundedIcon fontSize="medium" />, // fallback
      to: item.to,
      roles: item.roles,
    }));
  }
  // Se não tem config, usa o array navItems já existente
  return navItems;
}

const SidebarContent = ({
  onClose,
  collapsed,
  onToggleCollapse,
  showCollapseButton,
}: {
  onClose?: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  showCollapseButton?: boolean;
}) => {
  const location = useLocation();
  const theme = useTheme();
  const { logout, user } = useAuth();
  const menuItems = useSidebarMenuItems();

  const hasPermission = (roles: string[]) => {
    if (!user?.role) return false;
    return roles.some((role) => user.role.toLowerCase() === role.toLowerCase());
  };

  return (
    <Box
      width={250}
      bgcolor={theme.palette.background.paper}
      minHeight="100vh"
      boxShadow={3}
      display="flex"
      flexDirection="column"
      sx={{
        borderRight: `1.5px solid ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      <Box
        p={2}
        display="flex"
        alignItems="center"
        gap={1}
        justifyContent={'flex-start'}
        sx={{ userSelect: 'none', minHeight: 64, position: 'relative' }}
      >
        <img
          src="/assets/loading.png"
          alt="Logo"
          style={{
            width: 50,
            height: 50,
            marginRight: 4,
          }}
        />
        <Typography
          fontWeight={800}
          fontSize={18}
          color="primary.main"
          letterSpacing={-1}
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 160 }}
        >
          Controle de Acesso
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box width="100%" px={2} mb={1}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          Empresa
        </Typography>
        <Box sx={{ width: '100%' }}>
          <TenantSelectIfAdmin/>
        </Box>
      </Box>
      <List sx={{ px: 1 }}>
        {menuItems
          .filter((item) => hasPermission(item.roles))
          .map((item) => (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              selected={location.pathname === item.to}
              onClick={onClose}
              sx={{
                borderRadius: 2,
                my: 0.5,
                minHeight: 44,
                background: location.pathname === item.to ? '#E3EDFF' : 'transparent',
                color: location.pathname === item.to ? theme.palette.primary.main : theme.palette.text.primary,
                fontWeight: location.pathname === item.to ? 700 : 500,
                '& .MuiListItemIcon-root': {
                  color: location.pathname === item.to ? theme.palette.primary.main : theme.palette.text.primary,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.to ? 700 : 500,
                  fontSize: 16,
                  color: 'inherit',
                }}
              />
            </ListItemButton>
          ))}
      </List>
      <Box flex={1} />
      <Box px={2} mb={1}>
    
        <Button
          fullWidth
          variant="contained"
          startIcon={<LogoutRoundedIcon />}
          onClick={logout}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 16,
            bgcolor: '#FFF0F0',
            color: theme.palette.error.main,
            boxShadow: 'none',
            textTransform: 'none',
            mb: 1,
            py: 1.2,
            '&:hover': { bgcolor: '#FFE5E5' },
          }}
        >
          Sair
        </Button>
      </Box>
      <Box p={1} textAlign="center" fontSize={14} color="text.secondary">
        <span style={{ fontWeight: 500 }}>v1.0</span>
      </Box>
    </Box>
  );
};

const Sidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1200;

  return (
    <>

      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1301,
        }}
      >
        <IconButton onClick={() => setMobileOpen(true)} color="primary">
          <MenuIcon />
        </IconButton>
      </Box>


      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        <SidebarContent
          onClose={() => setMobileOpen(false)}
          collapsed={false}
          onToggleCollapse={() => { }}
          showCollapseButton={false}
        />
      </Drawer>


      <Box
        sx={{
          width: collapsed ? 72 : 250,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          minHeight: '100vh',
          transition: 'width 0.35s cubic-bezier(.4,0,.2,1)',
        }}
      >
        <SidebarContent
          collapsed={isDesktop ? collapsed : false}
          onToggleCollapse={() => setCollapsed((prev) => !prev)}
          showCollapseButton={isDesktop}
        />
      </Box>
    </>
  );
};

export default Sidebar;